import { useMemo } from "react";
import { KICK_INVOICE_SENDER_ADDRESS } from "@common/constants/invoices";
import { useSendingEmails } from "@api/sendingEmail.api";

// Sentinel option value for the default Kick sender (persisted as a null
// sendingEmailId, unlike custom addresses which persist their id).
export const KICK_SENDER_VALUE = "kick";

export interface SenderOption {
    value: string;
    email: string;
    displayName: string | null;
    // Entities this sender is scoped to. Empty/omitted means available to
    // every entity in the workspace (unscoped) - the Kick default option is
    // always unscoped. Callers that render more than one entity at a time
    // (the invoice's entity x sender matrix) must filter per entity using
    // this field rather than relying on a single pre-filtered list, since a
    // sender scoped to entity A must never appear for entity B.
    entityUuids?: string[];
}

// A sender with no entity scopes is available to every entity in the workspace
// (unscoped). A scoped sender is selectable only for the entities it lists,
// mirroring the backend send-path enforcement — so a sender scoped to entity A
// never appears (or stays selected) for an invoice belonging to entity B.
export function isSenderAvailableForEntity(
    entityUuids: string[] | undefined,
    entityUuid: string | undefined,
): boolean {
    if (!entityUuids || entityUuids.length === 0) {
        return true;
    }
    return entityUuid != null && entityUuids.includes(entityUuid);
}

export function useNativeInvoiceSenderAddress(
    sendingEmailId: string | null,
): string {
    const { data: sendingEmails } = useSendingEmails();
    if (sendingEmailId == null) {
        return KICK_INVOICE_SENDER_ADDRESS;
    }
    return (
        sendingEmails?.find((email) => email.id === sendingEmailId)?.email ??
        KICK_INVOICE_SENDER_ADDRESS
    );
}

// The selectable sender addresses (the Kick default plus every workspace
// verified custom address, each carrying its own entity scope) and the
// currently effective selection for `entityUuid` — a persisted id that is no
// longer selectable, or scoped away from this entity, falls back to the Kick
// default. `senderOptions` is intentionally NOT filtered down to `entityUuid`:
// callers rendering more than one entity at once (the From picker's entity x
// sender matrix) need every sender's own scope to filter correctly per
// entity, not just the currently selected one.
export function useNativeInvoiceSenderOptions(
    sendingEmailId: string | null,
    entityUuid?: string,
): {
    senderOptions: SenderOption[];
    selectedSenderValue: string;
} {
    const { data: sendingEmails } = useSendingEmails();

    const senderOptions = useMemo<SenderOption[]>(() => {
        const kickOption: SenderOption = {
            value: KICK_SENDER_VALUE,
            email: KICK_INVOICE_SENDER_ADDRESS,
            displayName: "Kick",
            entityUuids: [],
        };
        const customOptions = (sendingEmails ?? [])
            .filter((email) => email.isVerified)
            .map((email) => ({
                value: email.id,
                email: email.email,
                displayName: email.displayName,
                entityUuids: email.entityUuids,
            }));
        return [kickOption, ...customOptions];
    }, [sendingEmails]);

    const selectedSenderValue = useMemo(() => {
        const selected = senderOptions.find(
            (option) => option.value === sendingEmailId,
        );
        if (
            sendingEmailId != null &&
            selected &&
            isSenderAvailableForEntity(selected.entityUuids, entityUuid)
        ) {
            return sendingEmailId;
        }
        return KICK_SENDER_VALUE;
    }, [sendingEmailId, senderOptions, entityUuid]);

    return { senderOptions, selectedSenderValue };
}

// Once the sending emails have loaded, drop a persisted selection that is no
// longer selectable (deleted address, its domain became unverified, or it is
// scoped to entities that no longer include the invoice's From entity) so the
// form state falls back to the "Kick" default — otherwise Send would carry a
// stale sendingEmailId the backend rejects with a 400. This lives on the
// workspace so it runs on both steps, even if the user never touches the
// From entity picker before hitting Send.
export function useResetStaleSendingEmail({
    sendingEmailId,
    entityUuid,
    onChange,
}: {
    sendingEmailId: string | null;
    entityUuid?: string;
    onChange: (sendingEmailId: string | null) => void;
}): void {
    const { data: sendingEmails } = useSendingEmails();

    if (
        sendingEmails != null &&
        sendingEmailId != null &&
        !sendingEmails.some(
            (email) =>
                email.isVerified &&
                email.id === sendingEmailId &&
                isSenderAvailableForEntity(email.entityUuids, entityUuid),
        )
    ) {
        onChange(null);
    }
}
